import React, { useEffect, useMemo } from "react";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";

import { WidgetErrorState, WidgetLoadingState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";

import { LineChartConfigForm } from "./ConfigForm";
import { LineChart } from "@/components/dataviz/LineChartV2";
import { TimeSeriesWidget_someReqPropsUnset } from "./TimeSeriesWidget_processRawConfigFn";
import { TimeSeriesWidget_processRawConfigFn } from "./TimeSeriesWidget_processRawConfigFn";
import { useAggrMetricsAcrossTimeAndDimension } from "./query/useAggrMetricsAcrossTimeAndDimension";
import {
  calculateDataDateRange,
  generateTimeRangeOptions,
  filterDataByTimeRange,
  filterDataByCustomRange,
} from "./utils/timeRangeUtils";
import { MAX_POINTS } from "@/components/dataviz/LineChartV2/utils/responsivePoints";
import { WIDGET_MAX_HEIGHT } from "../../../constants";
import { buildHiddenMatcher, isHiddenDatapoint } from "../utils/filterHiddenDatapoints";


export const Component = ({
  contentType,
  dateValuePath,
  dateValuePathByContentType,
  dimensionValuePath,
  interval = { display: "Month", value: "month" },
  cyclical = { display: "No", value: false },
  selectedTimeRange,
  customTimeRange,
  id,
  widgetIdx = 0,
  activeFilters = [],
  actions,
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  hiddenDimDatapoints = [],
  data: _data,
  // public-share snapshot replay; falls back to the legacy `data` prop
  prefetchedData = _data
}) => {
  const allTpls = useGetQueryData(["allTpls"]);
  const { updateWidgetTimeRangeOptions, updateWidgetDataStore } = useReportLandingContext();

  const { data: dataFromApiCall, status: statusFromApiCall, error } = useAggrMetricsAcrossTimeAndDimension({
    vizCardId: id,
    contentType,
    dateValuePath,
    dateValuePathByContentType,
    dimensionValuePath,
    interval: interval?.value,
    cyclical: cyclical?.value,
    activeFilters: [
      ...activeFilters,
      ...presetActiveFilters,
      ...widgetFilters
    ],
    queryOptions: { enabled: prefetchedData ? false : true },
  });

  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? 'success' : statusFromApiCall;

  // Make latest chart data available to the config form (keyed by widget id)
  useEffect(() => {
    if (id && data) {
      updateWidgetDataStore?.(id, data);
    }
  }, [id, data, updateWidgetDataStore]);

  // Calculate data date range and generate time range options when data arrives
  useEffect(() => {
    if (data && data.length > 0) {
      const dateRange = calculateDataDateRange(data, 'timeInterval');
      if (dateRange) {
        const options = generateTimeRangeOptions(dateRange.spanDays);
        updateWidgetTimeRangeOptions(widgetIdx, options, dateRange.spanDays);
      }
    }
  }, [data, widgetIdx, updateWidgetTimeRangeOptions]);

  // Filter data by selected time range
  const timeFilteredData = useMemo(() => {
    if (!data || !selectedTimeRange || selectedTimeRange?.value === 'all') {
      return data;
    }
    if (selectedTimeRange?.value === 'custom') {
      return filterDataByCustomRange(data, customTimeRange, 'timeInterval');
    }
    return filterDataByTimeRange(data, selectedTimeRange?.value, 'timeInterval');
  }, [data, selectedTimeRange, customTimeRange]);

  // Filter out hidden breakdown dimensions. Hidden list may be plain strings
  // (manual mode) or {display,value} objects (dynamic Top-N/Bottom-N).
  const filteredData = useMemo(() => {
    if (!timeFilteredData || !hiddenDimDatapoints?.length) return timeFilteredData;
    const matcher = buildHiddenMatcher(hiddenDimDatapoints);
    return timeFilteredData.map(timePoint => ({
      ...timePoint,
      dimensions: timePoint.dimensions?.filter(dim => !isHiddenDatapoint(dim, matcher)),
    }));
  }, [timeFilteredData, hiddenDimDatapoints]);

  useUpdateLoadedWidgetsTracker({status, widgetIdx});

  const genViz = () => {
    if (!filteredData || filteredData.length === 0) {
      return <div>No data available</div>;
    }

    const isCustomRangeActive =
      selectedTimeRange?.value === "custom" && customTimeRange?.start && customTimeRange?.end;

    return (
      <LineChart
        data={filteredData}
        style={style}
        colorIdx={widgetIdx % 4}
        xAxisPath="timeInterval"
        height={WIDGET_MAX_HEIGHT}
        downsample={true}
        responsive={true}
        interval={interval?.value}
        // For custom range: keep downsampling but disable responsive-only by using fixed maxPoints
        maxPoints={isCustomRangeActive ? MAX_POINTS : undefined}
      />
    );
  };

  return (
    <div style={{ width: '100%' }}>
      {status === "loading" ? (
        <WidgetLoadingState/>
      ) : status === "error" ? (
        <WidgetErrorState error={error}/>
      ) : (
        status === "success" && genViz()
      )}
    </div>
  );
};

export const optionDef = { 
  display: "Line Chart", 
  icon: "Activity", 
  value: "widgetLineChart", 
  comp: 'WidgetLineChart' 
};

export const defaultConfig = {
  props: {
    interval: { display: "Month", value: "month" },
    cyclical: false,
    selectedTimeRange: { display: "All Data", value: "all" },
  }
};

export const processRawConfigFn = TimeSeriesWidget_processRawConfigFn;

export const ConfigForm = (props) => (
  <LineChartConfigForm
    {...props}
    widgetIcon={optionDef.icon}
    widgetTitle={optionDef.display}
  />
);

export const someReqPropsUnset = TimeSeriesWidget_someReqPropsUnset;

